/* eslint-disable indent */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import path from 'path';
import config from '../../../config';
import Caption from './Caption';
import Tags from '../../attachments/Tags';
import { getFileTypeImage, hasThumbnail } from '../../../util/fileTypeImages';

const isAbsoluteUrl = (url = '') => /^https?:\/\//.test(url);

const SelectedImage = ({
  index,
  margin,
  noMeta = false,
  onClick = () => true,
  onDetailTarget,
  onSetPrimary,
  onSetPublic,
  openAttachment,
  photo = {},
  renameAttachment,
  tags,
  count,
  progress,
}) => {
  const [currentSrc, setCurrentSrc] = useState(null);
  const sx = (100 - (30 / photo.width) * 100) / 100;
  const sy = (100 - (30 / photo.height) * 100) / 100;

  const imgStyle = photo.selected
    ? {
        transform: `translateZ(0px) scale3d(${sx}, ${sy}, 1)`,
        transition: 'transform .135s cubic-bezier(0.0,0.0,0.2,1),opacity linear .15s',
      }
    : {};

  const className = classNames('attachment-preview', {
    selected: photo.selected,
    loading: photo.uploading,
    'is-primary': photo.isPrimary,
    'is-public': photo.isPublic,
  });

  const checkMarkClick = event => {
    if (!photo.uploading) {
      event.checkmark = true;
      if (onClick) {
        onClick(event, { index, photo });
      }
    }
  };

  const handlePrimaryClick = event => {
    event.preventDefault();
    event.stopPropagation();
    onSetPrimary(photo);
  };

  const handlePublicClick = event => {
    event.preventDefault();
    event.stopPropagation();
    onSetPublic(photo.id);
  };

  const imageClick = event => {
    if (!photo.uploading) {
      event.checkmark = false;
      if (onClick) {
        onClick(event, { index, photo });
      }
    }
  };

  const handleLeftPaneClick = event => {
    event.stopPropagation();
    openAttachment(photo);
  };

  const handleRightPaneClick = event => {
    event.stopPropagation();
    onDetailTarget(photo.id);
  };

  const preloadImage = src => {
    const image = new Image();
    image.src = src;
    image.onload = () => {
      // setLoading(false);
      setCurrentSrc(src);
    };
  };

  const uploading = photo.uploading;

  const hasTags = photo.tags && photo.tags.length > 0;

  useEffect(() => {
    const useFile = photo.filename || photo.src;

    // Assume jpg if no extension
    const extension = path.extname(useFile).substring(1) || 'jpg';
    const fileIcon = getFileTypeImage(extension);

    // Usually Google
    if (isAbsoluteUrl(useFile)) {
      if (!photo.filename) {
        photo.filename = photo.src || path.basename(useFile) || 'jpg';
      }

      // Local & Hosted
    } else {
      if (!hasThumbnail(photo.mimeType)) {
        // eslint-disable-next-line
        photo.src = config.publicUrl(`img/${fileIcon}`);
        photo.width = 256;
        photo.height = 256;
      } else {
        if (photo.blob && !photo.uploading) {
          photo.src = photo.blob;
        } else if (!photo.src.startsWith('blob') && !photo.src.startsWith('data:')) {
          if (!photo.placeholder) {
            photo.placeholder = `${photo.src}/thumbnail?resize=60x`;
          }
          photo.src = `${photo.src}/thumbnail`;
        }
      }
    }

    // fallback filename to prevent error
    if (!photo.filename) {
      photo.filename = 'jpg';
    }

    // delete photo.mimeType;
    // delete photo.updatedBy;
    // delete photo.createdBy;
    // delete photo.createdAt;
    // delete photo.updatedAt;
    // delete photo.uploading;
    // delete photo.blob;
    // delete photo.isPrimary;
    // delete photo.isPublic;

    if (photo.placeholder) {
      preloadImage(photo.src);
    }

    setCurrentSrc(photo.placeholder || photo.src);
  }, [photo]);

  if (!currentSrc) {
    return null;
  }

  // If there is only 1 attachment, Gallery just files up the entire container, this prevents that
  if (count < 3) {
    if (photo.width && photo.height) {
      if (photo.width > photo.height) {
        // 476 is default thumbnail width
        const newWidth = Math.min(photo.width, 476);
        const newHeight = Math.round(photo.height * (newWidth / photo.width));
        photo.width = newWidth;
        photo.height = newHeight;
      } else {
        const newHeight = Math.min(photo.height, 400);
        const newWidth = Math.round(photo.width * (newHeight / photo.width));
        photo.width = newWidth;
        photo.height = newHeight;
      }
    }
  }

  let caption = '';
  if (photo.filename) {
    const filenameParts = photo.filename?.split('.');
    if (filenameParts.length > 1) {
      filenameParts.pop();
    }

    caption = filenameParts.join('.');
  }

  return (
    <div key={photo.key} style={{ margin, width: photo.width }} className={className}>
      <div className="loading-mask">
        <div className="progress-bar">
          <div className="progress" style={{ width: `${progress || 0}%` }} />
        </div>
      </div>

      <div className="top-shade">
        <div className="shade-left">
          <div className="select-icon" onClick={checkMarkClick} title="Select Attachment">
            <i className="fad fa-check-square" />
          </div>
        </div>
        {!noMeta && (
          <div className="shade-right">
            {hasTags && (
              <Tags attachmentTags={photo.tags.map(t => t.tagId)} tags={tags} stacked={photo.tags.length > 3} />
            )}
            <div
              className="primary-icon"
              onClick={handlePrimaryClick}
              title={photo.isPrimary ? 'Primary' : 'Mark As Primary'}
            >
              <i className="fa-duotone fa-star" />
            </div>
            <div
              className="public-icon ml-1"
              onClick={handlePublicClick}
              title={photo.isPublic ? 'Make Private' : 'Make Public'}
            >
              <i className="fa-duotone fa-globe" />
            </div>
          </div>
        )}
      </div>
      <div className="image-plane" onClick={imageClick} onMouseOver={photo.onMouseOver} onMouseOut={photo.onMouseOut}>
        <img style={imgStyle} alt={photo.filename} width={photo.width} height={photo.height} src={currentSrc} />
        {!noMeta && (
          <>
            <div className="left-pane" onClick={handleLeftPaneClick}>
              <i className="fas fa-external-link fa-fw" />
            </div>
            <div className="right-pane" onClick={handleRightPaneClick}>
              <i className="fas fa-info-square fa-fw" />
            </div>
          </>
        )}
      </div>
      {renameAttachment && !uploading && (
        <Caption label={caption} onChange={newName => renameAttachment(photo, newName)} />
      )}
    </div>
  );
};

SelectedImage.propTypes = {
  index: PropTypes.number,
  margin: PropTypes.number,
  onClick: PropTypes.func,
  photo: PropTypes.object,
  renameAttachment: PropTypes.func,
};

export default SelectedImage;
